/**
 * The socket, and the events it carries. Twin of `app/lib/net.dart`.
 *
 * Every message shape is imported from `shared/protocol.ts` directly — the
 * server and this client are one TypeScript program, and neither's protocol
 * definitions ever drift from the other's. The Dart mirror in the app is
 * kept in step with the same file by hand.
 */

import type {
  BotResultMessage,
  ClientMessage,
  LeaderboardEntry,
  MoveMessage,
  QueueMessage,
  ResignMessage,
  Role,
  ServerEvent as WireEvent,
} from '../../shared/protocol.js';

/**
 * A local widening of `ErrorEvent.code`. The server sends one of five known
 * codes today; a client that fails a type check on a new one would refuse
 * to render an error the server invented after the App Store cut. Widening
 * here rather than in the shared type keeps the server exhaustive.
 */
export interface ClientErrorEvent {
  type: 'error';
  code: string;
  message: string;
}

/** The socket dropped. Distinct from `over`: nothing was decided. */
export interface DisconnectedEvent {
  type: 'disconnected';
  cause?: unknown;
}

/**
 * Every event a listener will see. This is the union of `WireEvent` with
 * the widened error and the disconnect.
 */
export type ClientEvent =
  | Exclude<WireEvent, { type: 'error' }>
  | ClientErrorEvent
  | DisconnectedEvent;

/** Every ordinary listener the app registers. */
export type Listener = (event: ClientEvent) => void;

/**
 * Parse one frame off the wire. Never throws.
 *
 * A parser that threw would take the whole game down: the socket listener
 * is fed each frame in a callback, and an exception there escapes into the
 * event loop and unregisters the reader. The client would then never see
 * anything again on a socket that is still perfectly open. The Dart twin
 * makes the same promise for the same reason.
 */
export function decodeEvent(raw: string): ClientEvent {
  let j: unknown;
  try {
    j = JSON.parse(raw);
  } catch {
    return badFrame('not JSON');
  }
  if (j === null || typeof j !== 'object' || Array.isArray(j)) {
    return badFrame('frame is not an object');
  }

  const obj = j as Record<string, unknown>;
  const type = obj.type;
  switch (type) {
    case 'queued':
      return { type: 'queued' };
    case 'start':
      return {
        type: 'start',
        gameId: String(obj.gameId ?? ''),
        seed: asInt(obj.seed),
        size: asInt(obj.size),
        entranceCol: asInt(obj.entranceCol),
        role: (obj.role === 'bottom' ? 'bottom' : 'top') as Role,
        opponentName:
          typeof obj.opponentName === 'string' ? obj.opponentName : 'Player',
        you: asInt(obj.you),
        opponent: asInt(obj.opponent),
      };
    case 'state':
      return {
        type: 'state',
        you: asInt(obj.you),
        opponent: asInt(obj.opponent),
        youMoves: asInt(obj.youMoves),
        opponentMoves: asInt(obj.opponentMoves),
      };
    case 'over':
      return {
        type: 'over',
        result: obj.result === 'win' ? 'win' : 'loss',
        reason:
          obj.reason === 'resigned'
            ? 'resigned'
            : obj.reason === 'disconnected'
              ? 'disconnected'
              : 'reached',
        youMoves: asInt(obj.youMoves),
        opponentMoves: asInt(obj.opponentMoves),
      };
    case 'error':
      return {
        type: 'error',
        code: typeof obj.code === 'string' ? obj.code : 'unknown',
        message: typeof obj.message === 'string' ? obj.message : '',
      };
    case 'pong':
      return { type: 'pong' };
    case 'leaderboard':
      return {
        type: 'leaderboard',
        players: (Array.isArray(obj.players) ? obj.players : []).map(
          (raw): LeaderboardEntry => {
            const item = (raw ?? {}) as Record<string, unknown>;
            return {
              name: typeof item.name === 'string' ? item.name : 'Player',
              realGames: asInt(item.realGames),
              realWins: asInt(item.realWins),
              botGames: asInt(item.botGames),
              botWins: asInt(item.botWins),
            };
          },
        ),
      };
    default:
      return badFrame(`unrecognised event: ${String(type)}`);
  }
}

/**
 * JSON has one number type, so 15 and 15.0 are indistinguishable; the server
 * writes integers and this reads them safely from either. A non-number is
 * treated as zero rather than as a throw, so a truncated frame produces an
 * event with zeros rather than crashing the reader.
 */
function asInt(v: unknown): number {
  return typeof v === 'number' && Number.isFinite(v) ? Math.trunc(v) : 0;
}

function badFrame(message: string): ClientErrorEvent {
  return { type: 'error', code: 'bad-frame', message };
}

/**
 * One socket per app run, one game per socket at most.
 *
 * The class holds no game state — see `game.ts` for that. It exists to keep
 * `WebSocket` and its idiosyncrasies (retries, heartbeats, cancel-on-error)
 * out of the model, so the model can be tested with events it makes itself.
 */
export class GameClient {
  readonly endpoint: string;
  /**
   * How often to ping while otherwise idle.
   *
   * API Gateway drops a WebSocket after ten minutes without traffic. Four
   * minutes leaves margin for network hiccups and matches the Dart client
   * exactly. Injectable only so tests can run in milliseconds instead of
   * minutes.
   */
  readonly heartbeatMs: number;

  private socket: WebSocket | null = null;
  private ping: ReturnType<typeof setInterval> | null = null;
  private disposed = false;
  private readonly listeners = new Set<Listener>();

  constructor(endpoint: string, heartbeatMs = 4 * 60 * 1000) {
    this.endpoint = endpoint;
    this.heartbeatMs = heartbeatMs;
  }

  /** Subscribe. Returns a function that unsubscribes. */
  on(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  get isConnected(): boolean {
    return this.socket !== null && this.socket.readyState === WebSocket.OPEN;
  }

  /**
   * Open the socket, resolve when the handshake completes.
   *
   * Rejects on a refused connection rather than sitting in the "Connecting…"
   * state forever, which is what an app that returned early on a failed
   * open would do to a player trying to press Play again.
   */
  connect(): Promise<void> {
    if (this.disposed) return Promise.reject(new Error('client disposed'));
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      return Promise.resolve();
    }
    return new Promise((resolve, reject) => {
      let socket: WebSocket;
      try {
        socket = new WebSocket(this.endpoint);
      } catch (err) {
        reject(err);
        return;
      }
      const cleanup = (): void => {
        socket.removeEventListener('open', onOpen);
        socket.removeEventListener('error', onError);
      };
      const onOpen = (): void => {
        cleanup();
        this.socket = socket;
        socket.addEventListener('message', (ev) =>
          this.emit(decodeEvent(String(ev.data))),
        );
        socket.addEventListener('close', () => this.fail(undefined));
        socket.addEventListener('error', () =>
          this.fail(new Error('socket error')),
        );
        this.ping = setInterval(
          () => this.send({ action: 'ping' }),
          this.heartbeatMs,
        );
        resolve();
      };
      const onError = (): void => {
        cleanup();
        try {
          socket.close();
        } catch {
          // The socket never opened; there is nothing to close.
        }
        reject(new Error('could not open socket'));
      };
      socket.addEventListener('open', onOpen);
      socket.addEventListener('error', onError);
    });
  }

  /** Ask to be matched. `withFriend` names one specific person. */
  queue(name: string, withFriend?: string): void {
    const msg: QueueMessage = { action: 'queue', name };
    // Absent rather than blank, to match the Dart client — a blank `with` is
    // legitimately "no friend", but sending one confuses the two on the wire.
    if (withFriend !== undefined && withFriend !== '') msg.with = withFriend;
    this.send(msg);
  }

  move(dir: number): void {
    const msg: MoveMessage = { action: 'move', dir };
    this.send(msg);
  }

  resign(): void {
    const msg: ResignMessage = { action: 'resign' };
    this.send(msg);
  }

  requestLeaderboard(): void {
    this.send({ action: 'leaderboard' });
  }

  /**
   * Report the outcome of a client-side bot game.
   *
   * The web has no bot today — see docs/DEVELOPER_GUIDE.md for why the
   * client bot was deliberately not carried over. This method is kept so
   * that a future one can call it against the same protocol without a
   * schema migration.
   */
  reportBotResult(reportId: string, name: string, won: boolean): void {
    const msg: BotResultMessage = { action: 'bot-result', reportId, name, won };
    this.send(msg);
  }

  private send(message: ClientMessage): void {
    const socket = this.socket;
    if (!socket || socket.readyState !== WebSocket.OPEN) return;
    try {
      socket.send(JSON.stringify(message));
    } catch (err) {
      this.fail(err);
    }
  }

  private emit(event: ClientEvent): void {
    // Copy to an array so a listener that unsubscribes mid-iteration cannot
    // resize the Set the loop is over. `Set` is technically safe here in JS,
    // but the copy makes the property obvious and takes nothing away.
    for (const listener of [...this.listeners]) listener(event);
  }

  private fail(cause: unknown): void {
    this.teardown();
    this.emit({ type: 'disconnected', cause });
  }

  private teardown(): void {
    if (this.ping !== null) {
      clearInterval(this.ping);
      this.ping = null;
    }
    if (this.socket !== null) {
      try {
        this.socket.close();
      } catch {
        // The close race is expected; we've stopped listening.
      }
      this.socket = null;
    }
  }

  /** For tests: push an event as if it had come off the socket. */
  emitForTest(event: ClientEvent): void {
    this.emit(event);
  }

  dispose(): void {
    this.disposed = true;
    this.teardown();
    this.listeners.clear();
  }
}
